import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";

// ─────────────────────────────────────────────
// RiskHeatMap
//
// 5x5 ISO 31000 Likelihood x Impact matrix.
// Cell format: L{1-5}_I{1-5}
// Clickable cells. Posture labels at intersections.
// Design: §9.6 Risk Heat Map
// ─────────────────────────────────────────────

type CellId =
  | "L1_I1" | "L1_I2" | "L1_I3" | "L1_I4" | "L1_I5"
  | "L2_I1" | "L2_I2" | "L2_I3" | "L2_I4" | "L2_I5"
  | "L3_I1" | "L3_I2" | "L3_I3" | "L3_I4" | "L3_I5"
  | "L4_I1" | "L4_I2" | "L4_I3" | "L4_I4" | "L4_I5"
  | "L5_I1" | "L5_I2" | "L5_I3" | "L5_I4" | "L5_I5";

type RiskZone = "low" | "medium" | "high" | "very_high" | "critical";

// Cell color zones per design-system.md §9.6
const CELL_ZONE: Record<CellId, RiskZone> = {
  L1_I1: "low", L1_I2: "low", L1_I3: "medium", L1_I4: "high", L1_I5: "very_high",
  L2_I1: "low", L2_I2: "medium", L2_I3: "medium", L2_I4: "high", L2_I5: "very_high",
  L3_I1: "medium", L3_I2: "medium", L3_I3: "high", L3_I4: "high", L3_I5: "very_high",
  L4_I1: "high", L4_I2: "high", L4_I3: "high", L4_I4: "very_high", L4_I5: "very_high",
  L5_I1: "very_high", L5_I2: "very_high", L5_I3: "very_high", L5_I4: "critical", L5_I5: "critical",
};

// Count-badge text colour is picked per zone for WCAG-AA contrast against the
// saturated badge background: dark text on the lighter fills (green/amber/orange/
// violet), white on the dark red critical fill. White-on-amber/green/orange
// previously failed AA (~2–3:1).
const ZONE_COLORS: Record<RiskZone, { bg: string; border: string; count: string }> = {
  low: {
    bg: "bg-success-bg",
    border: "border-success/20",
    count: "bg-success text-black",
  },
  medium: {
    bg: "bg-warning-bg",
    border: "border-warning/20",
    count: "bg-warning text-black",
  },
  high: {
    bg: "bg-k-orange-bg",
    border: "border-k-orange/20",
    count: "bg-k-orange text-black",
  },
  very_high: {
    bg: "bg-k-purple-bg",
    border: "border-k-purple/20",
    count: "bg-k-purple text-black",
  },
  critical: {
    bg: "bg-danger-bg",
    border: "border-danger/20",
    count: "bg-danger text-white",
  },
};

// Translation keys for human-readable zone names (used in accessible labels
// and the legend). Resolved via t() at render time.
const ZONE_LABEL_KEYS: Record<RiskZone, string> = {
  low: "risk.low",
  medium: "risk.medium",
  high: "risk.high",
  very_high: "risk.veryHigh",
  critical: "risk.critical",
};

export interface HeatMapCell {
  cellId: CellId;
  count?: number;
  labels?: string[];
}

interface RiskHeatMapProps {
  cells?: HeatMapCell[];
  onCellClick?: (cellId: CellId) => void;
  selectedCell?: CellId | null;
  className?: string;
}

const LIKELIHOODS = [5, 4, 3, 2, 1] as const;
const IMPACTS = [1, 2, 3, 4, 5] as const;

export function RiskHeatMap({
  cells = [],
  onCellClick,
  selectedCell,
  className,
}: RiskHeatMapProps) {
  const { t } = useTranslation();

  const cellMap = new Map<string, HeatMapCell>(
    cells.map((c) => [c.cellId, c])
  );

  // Axis tick labels: numbers with translated qualifiers on the endpoints.
  const likelihoodLabel = (l: number) =>
    l === 5 ? `5 (${t("risk.high")})` : l === 1 ? `1 (${t("risk.low")})` : String(l);
  const impactLabel = (i: number) =>
    i === 1 ? `1 (${t("risk.low")})` : i === 5 ? `5 (${t("risk.critical")})` : String(i);

  return (
    <div className={cn("select-none", className)}>
      {/* Layout: [Y-axis label column] [matrix body] */}
      <div className="flex gap-0">
        {/* Y-axis column: "Likelihood" rotated title, spans the full height of the data rows */}
        <div className="flex flex-col" style={{ width: 40 }}>
          {/* Spacer to align with "Impact" title row + column header row */}
          <div style={{ height: 36 }} />
          {/* Rotated label spanning all 5 data rows */}
          <div className="flex flex-1 items-center justify-center">
            <span
              className="text-caption text-text-muted"
              style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", whiteSpace: "nowrap" }}
            >
              {t("risk.likelihood")}
            </span>
          </div>
        </div>

        {/* Right side: Impact title + column headers + rows */}
        <div className="flex-1">
          {/* "Impact" X-axis title */}
          <div className="mb-0.5 grid" style={{ gridTemplateColumns: "44px repeat(5, 1fr)" }}>
            <div />
            <div className="col-span-5 text-center text-caption font-medium text-text-secondary">
              {t("risk.impact")}
            </div>
          </div>

          {/* Column headers (Impact values) */}
          <div className="mb-1 grid" style={{ gridTemplateColumns: "44px repeat(5, 1fr)" }}>
            <div />
            {IMPACTS.map((i) => (
              <div key={i} className="px-1 text-center text-caption text-text-muted">
                {impactLabel(i)}
              </div>
            ))}
          </div>

          {/* Data rows */}
          {LIKELIHOODS.map((l) => (
            <div key={l} className="grid" style={{ gridTemplateColumns: "44px repeat(5, 1fr)" }}>
              {/* Per-row likelihood label */}
              <div className="flex items-center justify-end pr-2 text-caption text-text-muted min-h-[72px]">
                {likelihoodLabel(l)}
              </div>

              {/* Cells */}
              {IMPACTS.map((i) => {
                const id = `L${l}_I${i}` as CellId;
                const zone = CELL_ZONE[id];
                const colors = ZONE_COLORS[zone];
                const cellData = cellMap.get(id);
                const isSelected = selectedCell === id;
                const count = cellData?.count ?? 0;
                const countText =
                  count > 0 ? t("risk.heatCellCount", { count }) : t("risk.heatCellEmpty");
                const cellLabel = t("risk.heatCellLabel", {
                  likelihood: l,
                  impact: i,
                  zone: t(ZONE_LABEL_KEYS[zone]),
                  items: countText,
                });

                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => onCellClick?.(id)}
                    aria-label={cellLabel}
                    aria-pressed={onCellClick ? isSelected : undefined}
                    className={cn(
                      "relative flex min-h-[72px] flex-col items-center justify-center gap-1 border p-2 text-center transition-all",
                      colors.bg,
                      colors.border,
                      isSelected && "ring-2 ring-accent ring-inset",
                      onCellClick && "cursor-pointer hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent",
                      !onCellClick && "cursor-default"
                    )}
                    title={cellLabel}
                  >
                    {/* Count badge */}
                    {cellData?.count != null && cellData.count > 0 && (
                      <span
                        className={cn(
                          "flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold",
                          colors.count
                        )}
                      >
                        {cellData.count}
                      </span>
                    )}

                    {/* Labels */}
                    {cellData?.labels?.map((lbl, idx) => (
                      <span key={idx} className="max-w-full truncate text-[10px] text-text-muted">
                        {lbl}
                      </span>
                    ))}

                    {/* Cell ID (visible when empty) */}
                    {!cellData?.count && !cellData?.labels?.length && (
                      <span className="text-[10px] font-medium opacity-30" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                        {id}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="mt-3 flex flex-wrap gap-3">
        {(["low", "medium", "high", "very_high", "critical"] as RiskZone[]).map((zone) => (
          <div key={zone} className="flex items-center gap-1.5">
            <div className={cn("h-3 w-3 rounded-sm", ZONE_COLORS[zone].bg)} />
            <span className="text-caption text-text-muted">{t(ZONE_LABEL_KEYS[zone])}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export type { CellId, RiskZone };
